import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import type { ApplicationWithDetails, AttendeeProfileWithDetails, PostEventSurvey, SurveyKey } from '../types/database';
import './ApplicationDetailModal.css';
import { captureError } from '../lib/capture-error';
import { formatLocalDate } from '../lib/dates';

interface ApplicationDetailModalProps {
  application: ApplicationWithDetails | null;
  surveys?: Partial<Record<SurveyKey, PostEventSurvey>>;
  show: boolean;
  onHide: () => void;
  onStatusUpdate: (applicationId: string, status: string, notes?: string) => void;
  onResendNotification?: () => void;
  resendingNotification?: boolean;
  onDeleteApplication?: () => void;
  eventTitle?: string;
}

/**
 * Admin-only modal. Expects application from getApplicationsByEventDecrypted (applications Edge Function)
 * so attendee_profile is already populated. We do not fetch profile separately.
 */
const ApplicationDetailModal: React.FC<ApplicationDetailModalProps> = ({
  application,
  surveys,
  show,
  onHide,
  onStatusUpdate,
  onResendNotification,
  resendingNotification = false,
  onDeleteApplication,
  eventTitle,
}) => {
  const [detailedProfile, setDetailedProfile] = useState<AttendeeProfileWithDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    if (application && show) {
      try {
        // Safely cast the profile, handling malformed data
        const profile = application.attendee_profile;
        if (profile && typeof profile === 'object') {
          setDetailedProfile(profile as AttendeeProfileWithDetails);
          setError(null);
        } else {
          setDetailedProfile(null);
          if (!profile) {
            setError('Profile data not available for this application');
          }
        }
      } catch (err) {
        captureError(err, 'Error processing profile data');
        setDetailedProfile(null);
        setError('Failed to load profile data');
      }
      setAdminNotes(application.admin_notes || '');
      setNewStatus(application.status || 'submitted');
    }
  }, [application, show]);

  const handleStatusUpdate = () => {
    if (!application) return;
    onStatusUpdate(application.id, newStatus, adminNotes);
    onHide();
  };

  const getStatusBadge = (status: string) => {
    const statusClass = {
      draft: 'bg-secondary',
      submitted: 'bg-info',
      under_review: 'bg-warning',
      approved: 'bg-success',
      rejected: 'bg-danger',
      waitlisted: 'bg-warning',
    }[status] || 'bg-secondary';

    return <span className={`badge ${statusClass}`}>{status.replace('_', ' ')}</span>;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const surveyRows: Array<[SurveyKey, PostEventSurvey | undefined]> = [
    ['pre_event', surveys?.pre_event],
    ['post_event', surveys?.post_event],
  ];

  if (!application) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="application-detail-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          Application Review - {application.attendee_profile?.first_name} {application.attendee_profile?.last_name}
        </Modal.Title>
        <Button variant="outline-secondary" size="sm" onClick={handlePrint} className="ms-auto me-2 print-hide">
          <i className="fas fa-print me-1"></i>
          Print
        </Button>
      </Modal.Header>
      <Modal.Body className="application-print-content">
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {detailedProfile && (
          <div className="application-sections">
            {/* Personal Information Section */}
            <section className="application-section">
              <h4 className="section-header">Personal Information</h4>
              <div className="row">
                <div className="col-md-6">
                  <h5>Basic Information</h5>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td><strong>Name:</strong></td>
                        <td>{detailedProfile.first_name} {detailedProfile.last_name}</td>
                      </tr>
                      <tr>
                        <td><strong>Gender:</strong></td>
                        <td>{detailedProfile.gender || '—'}</td>
                      </tr>
                      <tr>
                        <td><strong>Date of Birth:</strong></td>
                        <td>{detailedProfile.date_of_birth ? formatLocalDate(detailedProfile.date_of_birth) : '—'}</td>
                      </tr>
                      <tr>
                        <td><strong>T-Shirt Size:</strong></td>
                        <td>{detailedProfile.tshirt_size || '—'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6">
                  <h5>Contact Information</h5>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td><strong>Email:</strong></td>
                        <td>{detailedProfile.email}</td>
                      </tr>
                      <tr>
                        <td><strong>Cell Phone:</strong></td>
                        <td>{detailedProfile.cell_phone}</td>
                      </tr>
                      <tr>
                        <td><strong>Home Phone:</strong></td>
                        <td>{detailedProfile.home_phone || '—'}</td>
                      </tr>
                      <tr>
                        <td><strong>Work Phone:</strong></td>
                        <td>{detailedProfile.work_phone || '—'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <h5 className="mt-4">Address</h5>
              <div className="bg-light p-3 rounded">
                <p className="mb-1">
                  {detailedProfile.address_line1}
                  {detailedProfile.address_line2 && <><br />{detailedProfile.address_line2}</>}
                </p>
                <p className="mb-0">
                  {detailedProfile.city}, {detailedProfile.state} {detailedProfile.zip}
                  {detailedProfile.country && <><br />{detailedProfile.country}</>}
                </p>
              </div>
            </section>

            {/* Emergency Contact Section */}
            <section className="application-section">
              <h4 className="section-header">Emergency Contact</h4>
              {detailedProfile.emergency_contact ? (
                <div>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td><strong>Name:</strong></td>
                        <td>{detailedProfile.emergency_contact.first_name} {detailedProfile.emergency_contact.last_name}</td>
                      </tr>
                      <tr>
                        <td><strong>Phone:</strong></td>
                        <td>{detailedProfile.emergency_contact.phone_number}</td>
                      </tr>
                    </tbody>
                  </table>

                  <h6>Address</h6>
                  <div className="bg-light p-3 rounded">
                    <p className="mb-1">
                      {detailedProfile.emergency_contact.address_line1}
                      {detailedProfile.emergency_contact.address_line2 && <><br />{detailedProfile.emergency_contact.address_line2}</>}
                    </p>
                    <p className="mb-0">
                      {detailedProfile.emergency_contact.city}, {detailedProfile.emergency_contact.state} {detailedProfile.emergency_contact.zip}
                      {detailedProfile.emergency_contact.country && <><br />{detailedProfile.emergency_contact.country}</>}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-muted">No emergency contact information provided.</p>
              )}
            </section>

            {/* Church Information Section */}
            <section className="application-section">
              <h4 className="section-header">Church Information</h4>
              {detailedProfile.church_info ? (
                <div>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td><strong>Church Name:</strong></td>
                        <td>{detailedProfile.church_info.church_name || '—'}</td>
                      </tr>
                      <tr>
                        <td><strong>Pastor:</strong></td>
                        <td>{detailedProfile.church_info.pastor_first_name} {detailedProfile.church_info.pastor_last_name}</td>
                      </tr>
                      <tr>
                        <td><strong>Phone:</strong></td>
                        <td>{detailedProfile.church_info.phone_number}</td>
                      </tr>
                    </tbody>
                  </table>

                  <h6>Church Address</h6>
                  <div className="bg-light p-3 rounded">
                    <p className="mb-1">
                      {detailedProfile.church_info.address_line1}
                      {detailedProfile.church_info.address_line2 && <><br />{detailedProfile.church_info.address_line2}</>}
                    </p>
                    <p className="mb-0">
                      {detailedProfile.church_info.city}, {detailedProfile.church_info.state} {detailedProfile.church_info.zip}
                      {detailedProfile.church_info.country && <><br />{detailedProfile.church_info.country}</>}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-muted">No church information provided.</p>
              )}
            </section>

            {/* References Section */}
            <section className="application-section">
              <h4 className="section-header">References</h4>
              {detailedProfile.personal_references && detailedProfile.personal_references.length > 0 ? (
                <div>
                  {detailedProfile.personal_references.map((ref) => (
                    <div key={ref.id} className="mb-4">
                      <h6>Reference #{ref.reference_number}</h6>
                      <table className="table table-sm">
                        <tbody>
                          <tr>
                            <td><strong>Name:</strong></td>
                            <td>{ref.first_name} {ref.last_name}</td>
                          </tr>
                          <tr>
                            <td><strong>Phone:</strong></td>
                            <td>{ref.phone_number}</td>
                          </tr>
                        </tbody>
                      </table>

                      <h6>Address</h6>
                      <div className="bg-light p-3 rounded">
                        <p className="mb-1">
                          {ref.address_line1}
                          {ref.address_line2 && <><br />{ref.address_line2}</>}
                        </p>
                        <p className="mb-0">
                          {ref.city}, {ref.state} {ref.zip}
                          {ref.country && <><br />{ref.country}</>}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No personal references provided.</p>
              )}
            </section>

            {/* Faith Information Section */}
            <section className="application-section">
              <h4 className="section-header">Faith Information</h4>
              {detailedProfile.faith_info ? (
                <div>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td><strong>Born Again Believer:</strong></td>
                        <td>
                          <span className={`badge ${detailedProfile.faith_info.is_born_again_believer ? 'bg-success' : 'bg-danger'}`}>
                            {detailedProfile.faith_info.is_born_again_believer ? 'Yes' : 'No'}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Length of Faith:</strong></td>
                        <td>{detailedProfile.faith_info.believer_length || '—'}</td>
                      </tr>
                      <tr>
                        <td><strong>Currently Practicing Evangelism:</strong></td>
                        <td>
                          <span className={`badge ${detailedProfile.faith_info.currently_practicing_evangelism ? 'bg-success' : 'bg-secondary'}`}>
                            {detailedProfile.faith_info.currently_practicing_evangelism ? 'Yes' : 'No'}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Previous Mission Trips:</strong></td>
                        <td>
                          <span className={`badge ${detailedProfile.faith_info.has_been_on_mission_trip ? 'bg-success' : 'bg-secondary'}`}>
                            {detailedProfile.faith_info.has_been_on_mission_trip ? 'Yes' : 'No'}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <h6>Personal Testimony</h6>
                  <div className="bg-light p-3 rounded">
                    <p className="mb-0">{detailedProfile.faith_info.personal_testimony || 'No testimony provided.'}</p>
                  </div>

                  {detailedProfile.faith_info.evangelism_experience && (
                    <>
                      <h6 className="mt-3">Evangelism Experience</h6>
                      <div className="bg-light p-3 rounded">
                        <p className="mb-0">{detailedProfile.faith_info.evangelism_experience}</p>
                      </div>
                    </>
                  )}

                  {detailedProfile.faith_info.mission_trip_experience && (
                    <>
                      <h6 className="mt-3">Mission Trip Experience</h6>
                      <div className="bg-light p-3 rounded">
                        <p className="mb-0">{detailedProfile.faith_info.mission_trip_experience}</p>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <p className="text-muted">No faith information provided.</p>
              )}
            </section>

            {/* Application Details Section */}
            <section className="application-section">
              <h4 className="section-header">Application Details</h4>
              <div className="row">
                <div className="col-md-6">
                  <h5>Application Information</h5>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td><strong>Status:</strong></td>
                        <td>{getStatusBadge(application.status)}</td>
                      </tr>
                      <tr>
                        <td><strong>Submitted:</strong></td>
                        <td>{formatDate(application.submitted_at)}</td>
                      </tr>
                      <tr>
                        <td><strong>Reviewed:</strong></td>
                        <td>{formatDate(application.reviewed_at)}</td>
                      </tr>
                      <tr>
                        <td><strong>Reviewed By:</strong></td>
                        <td>{application.reviewed_by?.full_name || '—'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6">
                  <h5>Custom Responses</h5>
                  {application.custom_field_responses && Object.keys(application.custom_field_responses).length > 0 ? (
                    <table className="table table-sm">
                      <tbody>
                        {Object.entries(application.custom_field_responses).map(([key, value]) => (
                          <tr key={key}>
                            <td><strong>{key.replace(/_/g, ' ')}:</strong></td>
                            <td>{String(value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-muted">No custom responses.</p>
                  )}
                </div>
              </div>

              {application.admin_notes && (
                <>
                  <h6 className="mt-3">Admin Notes</h6>
                  <div className="bg-light p-3 rounded">
                    <p className="mb-0">{application.admin_notes}</p>
                  </div>
                </>
              )}
            </section>

            <section className="application-section">
              <h4 className="section-header">Event Surveys</h4>
              <div className="row">
                {surveyRows.map(([surveyKey, survey]) => (
                  <div className="col-md-6 mb-3" key={surveyKey}>
                    <div className="border rounded p-3 h-100">
                      <h6 className="mb-2">{surveyKey === 'pre_event' ? 'Pre-Event Survey' : 'Post-Event Survey'}</h6>
                      {!survey ? (
                        <p className="text-muted mb-0">Not sent yet.</p>
                      ) : (
                        <>
                          <p className="mb-1">
                            <strong>Sent:</strong> {formatDate(survey.sent_at)}
                          </p>
                          <p className="mb-2">
                            <strong>Status:</strong> {survey.submitted_at ? 'Submitted' : survey.sent_at ? 'Sent (awaiting response)' : 'Not sent'}
                          </p>
                          {survey.submitted_at && (
                            <>
                              <p className="mb-2"><strong>Submitted:</strong> {formatDate(survey.submitted_at)}</p>
                              <pre className="bg-light p-2 rounded small mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(survey.response_payload || {}, null, 2)}
                              </pre>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="w-100">
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label">Status:</label>
              <select
                className="form-select"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="waitlisted">Waitlisted</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Admin Notes:</label>
              <textarea
                className="form-control"
                rows={2}
                placeholder="Add notes about this application..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
              />
            </div>
          </div>
          <div className="d-flex justify-content-between flex-wrap gap-2">
            <div className="d-flex gap-2">
              {onDeleteApplication && application.status === 'rejected' && (
                <Button variant="outline-danger" onClick={onDeleteApplication}>
                  <i className="fas fa-trash me-2" aria-hidden></i>
                  Delete Application
                </Button>
              )}
            </div>
            <div className="d-flex gap-2 ms-auto">
              {onResendNotification && application.status !== 'draft' && (
                <Button
                  variant="outline-secondary"
                  onClick={onResendNotification}
                  disabled={resendingNotification}
                >
                  <i className="fas fa-envelope me-2" aria-hidden></i>
                  {resendingNotification ? 'Sending...' : 'Resend Admin Email'}
                </Button>
              )}
              <Button variant="secondary" onClick={onHide}>
                <i className="fas fa-times me-2" aria-hidden></i>
                Close
              </Button>
              <Button variant="primary" onClick={handleStatusUpdate}>
                <i className="fas fa-save me-2" aria-hidden></i>
                Update Status & Notes
              </Button>
            </div>
          </div>
          {onDeleteApplication && application.status === 'rejected' && eventTitle && (
            <p className="text-muted small mb-0 mt-2">
              Deleting removes this application for {eventTitle} only. The attendee profile is kept so they can apply again.
            </p>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ApplicationDetailModal;
